import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import { ReducerStatus, RootState } from '../types';
import {
    MyBookingState,
    MyBookingCustomStatus,
    MyBookingPaymentStatus,
    MyBookingCreated,
    MyBookingPaymentInfo,
    MyBookingPaymentHighlight,
} from './types';
import {
    convertToDate,
    getDurationInDays,
    getDurationInHours,
    getShortDate,
    getTime,
} from '@shared/helpers/datetime/datetime';
import { round } from 'lodash';
import { expiredProduct, sortProducts } from '@/helpers/myBooking';
import {
    MyBookingStatus,
    Links,
    ImportantInformation,
    CustomerServiceMessage,
    UsedPaymentMethod,
    Coupon,
    AnyBookingProduct,
    ProductType,
    Money,
    FlightProduct,
    MyBooking,
    InformationType,
    BookingCancellationPolicy,
    MyBookingSpotlight,
    SpotlightType,
    Payment,
    FlightType,
} from '@/api/MyBooking/myBooking.interfaces';
import { isMainProduct } from '@/api/MyBooking/myBooking.helpers';
import { trackClarity } from '@/tracking/clarity';
import { ClarityEvent } from '@/tracking/clarity/types';
import { getLocaleDigitDateTime } from '@shared/helpers/datetime/datetime.formatters';
import { createSpotlights } from '@/components/myBookingPage/Sections/Spotlight/Spotlight.helpers';
import { BookingProductCombination } from '@shared/interfaces/productCombination';
import { getBookingProductCombination } from '@shared/helpers/productCombination';
import { getUpdatedMyBookingLinks } from '@/helpers/termsAndPoliciesLinks';

const initialState: MyBookingState = {
    status: ReducerStatus.INITIAL,
    data: null,
    refresh: false,
};

const state: MyBookingState = {
    ...initialState,
};

const getters: GetterTree<MyBookingState, RootState> = {
    getBookingId(state): string | undefined {
        return state.data?.bookingId;
    },

    getBookingNumber(state): string | undefined {
        return state.data?.bookingNumber;
    },

    getBookingPassword(state): string | undefined {
        return state.data?.password;
    },

    getProducts(state) {
        return state.data?.products ?? [];
    },

    getProductCombination(state): BookingProductCombination | null {
        return state.data?.productCombination || null;
    },

    getHotelProducts(state, getter) {
        return getter.getProducts.filter(
            (p: AnyBookingProduct) => p.type === ProductType.Hotel
        );
    },

    getFlightProducts(state, getter) {
        return getter.getProducts.filter(
            (p: AnyBookingProduct) => p.type === ProductType.Flight
        );
    },

    getFlightRyanairProducts(state, getter) {
        return getter.getProducts.filter(
            (p: AnyBookingProduct) =>
                p.type === ProductType.Flight &&
                p.ticket.type === FlightType.Ryanair &&
                p.ticket.verification // Not allowed to be null/undefined for Ryanair
        );
    },

    getFerryProducts(state, getter) {
        return getter.getProducts.filter(
            (p: AnyBookingProduct) => p.type === ProductType.Ferry
        );
    },

    getMainProducts(state): AnyBookingProduct[] {
        const products = state.data?.products ?? [];
        return [...products].filter((x) => isMainProduct(x.type));
    },

    getMainProductsIds(state, getter): string[] {
        return getter.getMainProducts.map((p: AnyBookingProduct) => {
            switch (p.type) {
                case ProductType.Flight:
                    return p.ticket.pnr;
                case ProductType.Ferry:
                    return p.productId;
                case ProductType.Transfer:
                    return p.productId;
                case ProductType.Hotel:
                    return p.rooms.map((x) => x.productId);
            }
        });
    },

    getFlightProductByPnr: (state, getter) => (pnr: string) =>
        getter.getFlightProducts.find((flight: FlightProduct) => flight.ticket.pnr === pnr),

    getServicePackageProduct(state, getter) {
        const servicePackageTypes = new Set([
            ProductType.ServicePackageOld,
            ProductType.ServicePackageBasic,
            ProductType.ServicePackagePremium,
            ProductType.ServicePackageAccommodation,
        ]);

        return getter.getProducts.find((p: AnyBookingProduct) =>
            servicePackageTypes.has(p.type)
        );
    },

    getCancellationInsuranceProducts(state, getter) {
        return getter.getProducts.filter(
            (p: AnyBookingProduct) => p.type === ProductType.CancellationInsurance
        );
    },

    getBaggageInsuranceProducts(state, getter) {
        return getter.getProducts.filter(
            (p: AnyBookingProduct) => p.type === ProductType.BaggageInsurance
        );
    },

    getSpotlights(state): MyBookingSpotlight[] {
        return state.data?.spotlights ?? [];
    },

    getSpotlight: (_, getter) => (position: number) => {
        return getter.getSpotlights.find((x: MyBookingSpotlight) => x.position === position);
    },

    getSpotlightPositionByType: (_, getter) => (type: SpotlightType) => {
        return getter.getSpotlights.find((x: MyBookingSpotlight) => x.type === type)
            ?.position;
    },

    getAmountAlreadyPaid(state): Money | null {
        return state.data?.payment?.amountAlreadyPaid ?? null;
    },
    getRemainingToBePaid(state): Money | null {
        return state.data?.payment?.remainingToBePaid ?? null;
    },
    getRemainingDownPaymentToBePaid(state): Money | null {
        return state.data?.payment?.remainingDownPaymentToBePaid ?? null;
    },
    getUsedPaymentMethods(state): UsedPaymentMethod[] {
        return state.data?.payment?.usedPaymentMethods ?? [];
    },

    getCoupons(state): Coupon[] | null {
        return state.data?.coupons ?? null;
    },

    getCustomerServiceMessages(state): CustomerServiceMessage[] {
        const messages = state.data?.customerServiceMessages ?? [];

        // FE will filter out anything with a productId for now since BE is returning "Invoice" category type.
        // They need to return the "Invoice" category due to general messages has the same category.
        return messages.filter((x) => !x.productId);
    },

    getPaymentDueTime(state): Date | null {
        const d = state.data?.payment?.paymentDueTime ?? null;
        return d ? convertToDate(d) : null;
    },
    getDownPaymentDueTime(state): Date | null {
        const d = state.data?.payment?.downPaymentDueTime ?? null;
        return d ? convertToDate(d) : null;
    },

    getLinks(state): Links | null {
        const links = state.data?.links;

        return links ? getUpdatedMyBookingLinks(links) : null;
    },
    getImportantInformation(state): ImportantInformation[] {
        return state.data?.importantInformation ?? [];
    },
    getCancellationPolicies(state): BookingCancellationPolicy[] {
        return state.data?.cancellationPolicies ?? [];
    },

    getBookingCreated(state): MyBookingCreated | null {
        const d = state.data?.created;

        if (d) {
            const date = convertToDate(d);

            return {
                date,
                fullDate: getLocaleDigitDateTime(date),
                shortDate: getShortDate(date),
                time: getTime(date),
                isToday: getDurationInHours(new Date(), date, false) <= 24,
            };
        }

        return null;
    },

    getPaymentStatus(_, getter): MyBookingPaymentStatus {
        const HOURS_CLOSE_PAYMENT = 48;
        const now = new Date();

        const alreadyPaid = getter.getAmountAlreadyPaid;

        const getFinalPayment = (): MyBookingPaymentInfo | null => {
            const amount = getter.getRemainingToBePaidAmount;
            const dueTime = getter.getPaymentDueTime;
            return mapPayment(amount, dueTime);
        };

        const getDownPayment = (): MyBookingPaymentInfo | null => {
            const amount = getter.getRemainingDownPaymentToBePaidAmount;
            const dueTime = getter.getDownPaymentDueTime;
            return mapPayment(amount, dueTime);
        };

        const mapPayment = (amount: Money, dueTime: Date): MyBookingPaymentInfo | null => {
            if (!amount || !dueTime) {
                return null;
            }

            const hours = getDurationInHours(dueTime, now, false);
            const days = getDurationInDays(dueTime, now, false);
            const hasPaymentRemaining = amount.amount > 0 && hours >= 0;
            return hasPaymentRemaining ? { amount, dueTime, hours, days } : null;
        };

        const finalPayment = getFinalPayment();
        const downPayment = getDownPayment();
        const hasPaymentRemaining = Boolean(finalPayment || downPayment);

        const highlight: MyBookingPaymentHighlight = {
            finalPaymentDueSoon: Boolean(
                finalPayment && finalPayment.hours <= HOURS_CLOSE_PAYMENT
            ),
            downPaymentDueSoon: Boolean(
                downPayment && downPayment.hours <= HOURS_CLOSE_PAYMENT
            ),
        };

        return {
            hasPaymentRemaining,
            highlight,
            alreadyPaid,
            finalPayment,
            downPayment,
        };
    },

    getRefresh(state): boolean {
        return state.refresh ?? false;
    },

    getCanDisplayPaymentReminder(state): boolean {
        return Boolean(state.data?.canDisplayPaymentReminder);
    },

    getCustomStatus(state): MyBookingCustomStatus {
        return state.data?.customStatus ?? MyBookingCustomStatus.AwaitingCreation;
    },

    getIsBookingExpired(state): boolean {
        return isExpiredBooking(state.data);
    },

    getIsBookingCancelled(state): boolean {
        return isCancelledBooking(state.data);
    },

    getIsBookingCreated(state): boolean {
        return isCreatingBooking(state.data);
    },

    getIsBookingActive(state): boolean {
        return isActiveBooking(state.data);
    },

    getCurrency(_, getter): string {
        return getter.getAmountAlreadyPaid?.currency ?? getter.getRemainingToBePaid?.currency;
    },

    getAmountAlreadyPaidAmount(_, getter): Money {
        return getAmount(getter.getAmountAlreadyPaid);
    },

    getRemainingToBePaidAmount(_, getter): Money {
        return getAmount(getter.getRemainingToBePaid);
    },

    getRemainingDownPaymentToBePaidAmount(_, getter): Money {
        return getAmount(getter.getRemainingDownPaymentToBePaid);
    },

    getTotalBookingValue(_, getter): Money {
        return {
            amount:
                getter.getAmountAlreadyPaidAmount.amount +
                getter.getRemainingToBePaidAmount.amount,
            currency: getter.getCurrency,
        };
    },
};

const actions: ActionTree<MyBookingState, RootState> = {
    removeSpotlight({ state, commit }, payload: { position: number; rearrange: boolean }) {
        if (state.data?.spotlights) {
            const updatedSpotlights = state.data.spotlights
                .filter((spotlight) => spotlight.position !== payload.position)
                .map((spotlight, index) => ({
                    ...spotlight,
                    position: payload.rearrange ? index + 1 : spotlight.position,
                }));

            commit('setState', {
                ...state,
                data: { ...state.data, spotlights: updatedSpotlights },
            });
        }
    },

    setMyBookingLoading({ state, commit }) {
        commit('setState', {
            ...state,
            status: ReducerStatus.LOADING,
            refresh: false,
        });
    },

    setMyBookingUpdating({ state, commit }) {
        commit('setState', {
            ...state,
            status: ReducerStatus.UPDATING,
            refresh: false,
        });
    },

    async setMyBookingSuccess({ state, commit }, payload) {
        commit('setState', {
            ...state,
            status: ReducerStatus.LOADED,
            data: await mapBookingData(payload),
            refresh: false,
        });
    },

    setMyBookingError({ state, commit }) {
        trackClarity(ClarityEvent.MY_BOOKING_INITIAL_ERROR);

        commit('setState', {
            ...state,
            status: ReducerStatus.ERROR,
            data: null,
            refresh: false,
        });
    },

    updateFlightProducts({ state, commit }, payload: FlightProduct[]) {
        const current = state.data?.products ?? [];

        const updated = current.map((p: AnyBookingProduct) => {
            if (p.type === ProductType.Flight) {
                const updated = payload.find((u) => u.ticket.pnr === p.ticket.pnr);

                if (updated) {
                    return {
                        ...p,
                        ticket: {
                            ...p.ticket,
                            verification: updated.ticket?.verification,
                        },
                    };
                }
            }
            return p;
        });

        commit('setState', {
            ...state,
            data: { ...state.data, products: updated },
        });
    },

    forceRefresh({ state, commit }) {
        commit('setState', {
            ...state,
            refresh: true,
        });
    },

    clearState({ commit }) {
        commit('clearState', initialState);
    },
};

const mutations: MutationTree<MyBookingState> = {
    setState(state, payload) {
        state.status = payload.status;
        state.data = payload.data;
        state.refresh = payload.refresh;
    },
    clearState(state, payload) {
        Object.assign(state, payload);
    },
};

export const myBooking: Module<MyBookingState, RootState> = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export function hasRemainingPayment(payment: Payment | null): boolean {
    if (!payment) return false;
    const remainingToBePaid = getAmount(payment?.remainingToBePaid);
    const remainingDownpaymentToBePaid = getAmount(payment?.remainingDownPaymentToBePaid);

    return remainingToBePaid.amount > 0 || remainingDownpaymentToBePaid.amount > 0;
}

export const createCustomStatus = (
    status: MyBookingStatus,
    hasRemainingPayment: boolean
): MyBookingCustomStatus => {
    switch (status) {
        case MyBookingStatus.Completed: {
            if (!hasRemainingPayment) {
                return MyBookingCustomStatus.ConfirmedAndPaid;
            } else {
                return MyBookingCustomStatus.Confirmed;
            }
        }

        case MyBookingStatus.AwaitingOk:
            return MyBookingCustomStatus.Confirmed;

        case MyBookingStatus.AwaitingOkVerification:
        case MyBookingStatus.AwaitingOkFailedProduct:
            return MyBookingCustomStatus.ConfirmedAndAwaiting;

        case MyBookingStatus.Cancelled:
        case MyBookingStatus.Aborted:
            return MyBookingCustomStatus.Cancelled;

        default:
            return MyBookingCustomStatus.AwaitingCreation;
    }
};

export const createPaymentStatus = (
    amountAlreadyPaid: Money | null,
    finalPaymentRemaining: Money | null,
    finalPaymentDueTime: string | null,
    downPaymentRemaining: Money | null,
    downPaymentDueTime: string | null
): MyBookingPaymentStatus => {
    const HOURS_CLOSE_PAYMENT = 48;
    const now = new Date();

    const alreadyPaid = getAmount(amountAlreadyPaid);

    const getFinalPayment = (): MyBookingPaymentInfo | null => {
        const amount = getAmount(finalPaymentRemaining);
        const dueTime = finalPaymentDueTime ? convertToDate(finalPaymentDueTime) : null;

        return mapPayment(amount, dueTime);
    };

    const getDownPayment = (): MyBookingPaymentInfo | null => {
        const amount = getAmount(downPaymentRemaining);
        const dueTime = downPaymentDueTime ? convertToDate(downPaymentDueTime) : null;

        return mapPayment(amount, dueTime);
    };

    const mapPayment = (amount: Money, dueTime: Date | null): MyBookingPaymentInfo | null => {
        if (!amount || !dueTime) {
            return null;
        }

        const hours = getDurationInHours(dueTime, now, false);
        const days = getDurationInDays(dueTime, now, false);
        const hasPaymentRemaining = amount.amount > 0 && hours >= 0;
        return hasPaymentRemaining ? { amount, dueTime, hours, days } : null;
    };

    const finalPayment = getFinalPayment();
    const downPayment = getDownPayment();
    const hasPaymentRemaining = Boolean(finalPayment || downPayment);

    const highlight: MyBookingPaymentHighlight = {
        finalPaymentDueSoon: Boolean(
            finalPayment && finalPayment.hours <= HOURS_CLOSE_PAYMENT
        ),
        downPaymentDueSoon: Boolean(downPayment && downPayment.hours <= HOURS_CLOSE_PAYMENT),
    };

    return {
        hasPaymentRemaining,
        highlight,
        alreadyPaid,
        finalPayment,
        downPayment,
    };
};

const mapBookingData = async (payload: MyBooking): Promise<MyBooking> => {
    const convertedProducts = payload.products.map((product: AnyBookingProduct) => {
        if (product.type === ProductType.ServicePackageOld) {
            return {
                ...product,
                type: ProductType.ServicePackageBasic,
            } as AnyBookingProduct;
        }
        return product;
    });

    const products = sortProducts([...convertedProducts]);

    const mainProducts = products.filter((p) => isMainProduct(p.type));
    const expired = expiredProduct(mainProducts[mainProducts.length - 1]);

    const supportedImportantInformation = [
        InformationType.ETAProgram,
        InformationType.VisaWaiverProgramWarning,
        InformationType.UKVisaWarning,
        InformationType.ThailandArrivalCardWarning,
    ];

    const importantInformation: ImportantInformation[] = [
        ...payload.importantInformation,
    ].filter((x) => supportedImportantInformation.includes(x.type));

    const customStatus = createCustomStatus(
        payload.status,
        hasRemainingPayment(payload.payment)
    );

    const data: MyBooking = {
        ...payload,
        customStatus,
        products,
        expired,
        importantInformation,
    };

    const productTypes = data.products.map((x) => x.type);
    data.productCombination = getBookingProductCombination(productTypes);

    data.spotlights = await createSpotlights(data);

    return data;
};

const getAmount = (payment: Money | null | undefined): Money => {
    return {
        amount: payment ? round(payment?.amount, 2) : 0,
        currency: payment?.currency ?? '',
    };
};

const isCreatingBooking = (booking: MyBooking | null): boolean => {
    return booking?.customStatus === MyBookingCustomStatus.AwaitingCreation;
};

const isCancelledBooking = (booking: MyBooking | null): boolean => {
    return booking?.customStatus === MyBookingCustomStatus.Cancelled;
};

export const isActiveBooking = (booking: MyBooking | null): boolean => {
    return !isCreatingBooking(booking) && !isCancelledBooking(booking);
};

export const isExpiredBooking = (booking: MyBooking | null): boolean => {
    return booking?.expired ?? false;
};
